/**
 * ReviewSummaryCard Component
 *
 * Displays comprehensive review summary for guide dashboard including:
 * - Average rating with star visualization
 * - Total reviews with growth indicator
 * - Category rating breakdowns (safety, communication, value, experience)
 * - Recent reviews preview (3 most recent)
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-09: Review Summary (3 SP)
 *
 * Props:
 * - satisfaction: object with avg_rating, total_reviews, reviews_this_month, reviews_last_month, review_growth_percent, ratings
 * - recentReviews: array of recent review objects
 * - loading: boolean
 * - onViewAll: function to navigate to full reviews page
 */

import React from 'react';
import Avatar from '../common/Avatar';
import PropTypes from 'prop-types';
import { Star as _Star, TrendingUp as _TrendingUp, TrendingDown, ChevronRight, MessageSquare, AlertCircle } from 'lucide-react';

/**
 * Render star rating visualization
 */
function StarRating({ rating, size = 20, showValue = true }) {
  const safeRating = Math.max(0, Math.min(5, Number(rating) || 0));
  const fullStars = Math.floor(safeRating);
  const hasHalfStar = safeRating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex items-center space-x-1">
      {/* Full stars */}
      {[...Array(fullStars)].map((_, i) => (
        <_Star key={`full-${i}`} size={size} className="fill-yellow-400 text-yellow-400" />
      ))}

      {/* Half star */}
      {hasHalfStar && (
        <div className="relative" style={{ width: size, height: size }}>
          <_Star size={size} className="text-gray-300 absolute" />
          <div style={{ width: '50%', overflow: 'hidden' }}>
            <_Star size={size} className="fill-yellow-400 text-yellow-400" />
          </div>
        </div>
      )}

      {/* Empty stars */}
      {[...Array(emptyStars)].map((_, i) => (
        <_Star key={`empty-${i}`} size={size} className="text-gray-300" />
      ))}

      {showValue && (
        <span className="ml-2 text-lg font-semibold text-gray-900">{safeRating.toFixed(1)}</span>
      )}
    </div>
  );
}

/**
 * Render category rating bar
 */
function CategoryRatingBar({ label, rating, maxRating = 5 }) {
  const safeRating = Math.max(0, Math.min(5, Number(rating) || 0));
  const percentage = (safeRating / maxRating) * 100;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="text-gray-900 font-semibold">{safeRating.toFixed(1)}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-green-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Render individual review preview
 */
function ReviewPreview({ review }) {
  const { overall_rating, comment, first_name, last_name, created_at } = review;

  // Format date
  const date = new Date(created_at);
  const timeAgo = getTimeAgo(date);

  // Truncate comment to 120 characters
  const truncatedComment = comment && comment.length > 120
    ? `${comment.substring(0, 120)}...`
    : comment;

  return (
    <div className="border-l-4 border-blue-500 pl-4 py-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Avatar
            name={`${first_name || ''} ${last_name || ''}`.trim()}
            size="sm"
          />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {first_name} {last_name}
            </p>
            <p className="text-xs text-gray-500">{timeAgo}</p>
          </div>
        </div>
        <StarRating rating={overall_rating} size={14} showValue={false} />
      </div>
      {truncatedComment && (
        <p className="text-sm text-gray-600 italic">&quot;{truncatedComment}&quot;</p>
      )}
    </div>
  );
}

/**
 * Helper: Calculate time ago string
 */
function getTimeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {return 'Today';}
  if (diffDays === 1) {return 'Yesterday';}
  if (diffDays < 7) {return `${diffDays} days ago`;}
  if (diffDays < 30) {return `${Math.floor(diffDays / 7)} weeks ago`;}
  if (diffDays < 365) {return `${Math.floor(diffDays / 30)} months ago`;}
  return `${Math.floor(diffDays / 365)} years ago`;
}

export default function ReviewSummaryCard({
  satisfaction,
  recentReviews = [],
  loading = false,
  onViewAll
}) {
  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!satisfaction || satisfaction.total_reviews === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <span>Reviews</span>
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <_Star className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No reviews yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Reviews from customers will appear here after completed trips.
          </p>
        </div>
      </div>
    );
  }

  const {
    avg_rating,
    total_reviews,
    reviews_this_month,
    reviews_last_month,
    review_growth_percent,
    ratings
  } = satisfaction;

  const isGrowthPositive = review_growth_percent > 0;
  const GrowthIcon = isGrowthPositive ? _TrendingUp : TrendingDown;
  const growthColor = isGrowthPositive ? 'text-green-600' : 'text-red-600';

  // Calculate count of reviews awaiting response (MVP 1.21.3 - US-1.21.3-11)
  const awaitingResponseCount = recentReviews.filter(review => !review.guide_response).length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-gray-600" />
              <span>Reviews</span>
            </h2>
            {/* Awaiting Response Badge (MVP 1.21.3 - US-1.21.3-11) */}
            {awaitingResponseCount > 0 && (
              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
                <AlertCircle className="w-4 h-4" />
                <span>{awaitingResponseCount} Awaiting Response</span>
              </span>
            )}
          </div>
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="flex items-center space-x-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              <span>View All</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Average Rating Section */}
        <div className="flex items-center justify-between">
          <div>
            <StarRating rating={avg_rating} size={24} showValue={true} />
            <p className="text-sm text-gray-500 mt-2">
              Based on {total_reviews} {total_reviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          {/* Growth Indicator */}
          {review_growth_percent !== 0 && (
            <div className={`flex items-center space-x-1 ${growthColor}`}>
              <GrowthIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">
                {Math.abs(review_growth_percent).toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        {/* Monthly Stats */}
        <div className="flex items-center space-x-4 text-sm">
          <div>
            <span className="text-gray-600">This month: </span>
            <span className="font-semibold text-gray-900">{reviews_this_month}</span>
          </div>
          <div className="text-gray-300">|</div>
          <div>
            <span className="text-gray-600">Last month: </span>
            <span className="font-semibold text-gray-900">{reviews_last_month}</span>
          </div>
        </div>

        {/* Category Ratings */}
        {ratings && (
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Rating Breakdown
            </h3>
            <CategoryRatingBar label="Safety" rating={ratings.safety} />
            <CategoryRatingBar label="Communication" rating={ratings.communication} />
            <CategoryRatingBar label="Value" rating={ratings.value} />
            <CategoryRatingBar label="Experience" rating={ratings.experience} />
          </div>
        )}

        {/* Recent Reviews */}
        {recentReviews && recentReviews.length > 0 && (
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Recent Reviews
            </h3>
            <div className="space-y-3">
              {recentReviews.slice(0, 3).map((review) => (
                <ReviewPreview key={review.id} review={review} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ReviewSummaryCard.propTypes = {
  satisfaction: PropTypes.shape({
    avg_rating: PropTypes.number,
    total_reviews: PropTypes.number,
    reviews_this_month: PropTypes.number,
    reviews_last_month: PropTypes.number,
    review_growth_percent: PropTypes.number,
    ratings: PropTypes.shape({
      safety: PropTypes.number,
      communication: PropTypes.number,
      value: PropTypes.number,
      experience: PropTypes.number,
    }),
  }),
  recentReviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      overall_rating: PropTypes.number,
      comment: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      created_at: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  onViewAll: PropTypes.func,
};

StarRating.propTypes = {
  rating: PropTypes.number,
  size: PropTypes.number,
  showValue: PropTypes.bool,
};

CategoryRatingBar.propTypes = {
  label: PropTypes.string.isRequired,
  rating: PropTypes.number.isRequired,
  maxRating: PropTypes.number,
};

ReviewPreview.propTypes = {
  review: PropTypes.shape({
    overall_rating: PropTypes.number,
    comment: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
};
